/*
  TYOHAAR rail arrows.

  One control for every horizontal rail in the theme — hampers, gifting,
  price bands, Instagram, the occasion rails and tiles, the two PDP rails,
  People also buy, and the review photo strip. They were all the same shape
  already: a flex row with `overflow-x: auto` and scroll snapping, scrolled by
  a finger on a phone and by nothing at all on a desktop.

  WHY ONE COMPONENT AND NOT ELEVEN
    Eleven copies drift. The one that matters here is the overflow test: a
    rail must show arrows only when there is something to scroll to, and
    getting that wrong in one place is a pair of dead buttons nobody notices
    for a month. It is written once, below.

  WHAT IT DOES NOT DO
    Move anything in the DOM, or touch the rail's own styles. The rail keeps
    scrolling exactly as it did; this reads it and scrolls it. Turn the
    arrows off and the section is byte for byte what it was.

  EXPECTED MARKUP — the snippet renders this directly AFTER the rail, inside
  the rail's own parent:

    <ul class="hmp__rail"> ... </ul>
    <tyoh-rail class="rail-nav" data-rail=".hmp__rail">
      <button class="rail-nav__btn rail-nav__btn--prev" data-rail-prev>
      <button class="rail-nav__btn rail-nav__btn--next" data-rail-next>
    </tyoh-rail>
*/

if (!customElements.get('tyoh-rail')) {
  class TyohRail extends HTMLElement {
    connectedCallback() {
      const selector = this.dataset.rail;
      if (!selector) return;

      /*
        The rail is found inside this element's own parent rather than inside
        the section. A section can hold more than one rail — the occasion
        rails and the PDP both do — and a document-wide or section-wide
        lookup would hand every control the first one it found.
      */
      this.rail = this.parentElement?.querySelector(selector);
      if (!this.rail) return;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.prev || !this.next) return;

      // The parent becomes the positioning context for the two buttons. Done
      // here rather than in a stylesheet because the parent is a different
      // element in every section and has no class in common.
      this.parentElement.classList.add('rail-nav-host');

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onScroll = () => this.schedule();

      this.prev.addEventListener('click', this.onPrev);
      this.next.addEventListener('click', this.onNext);
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });

      /*
        ResizeObserver on the rail AND on its first item.

        The rail alone is not enough. A card that reflows — an image arriving,
        a product title wrapping to a second line, a font swapping in — changes
        the scroll width without changing the rail's own box, and the arrows
        would then be answering a question that was asked before the content
        settled.
      */
      this.observer = new ResizeObserver(this.onScroll);
      this.observer.observe(this.rail);
      if (this.rail.firstElementChild) this.observer.observe(this.rail.firstElementChild);

      this.update();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.rail?.removeEventListener('scroll', this.onScroll);
      this.observer?.disconnect();
    }

    // Scroll fires many times a frame. One read per frame is plenty, and it
    // keeps the measuring out of the scroll handler itself.
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    /*
      HOW FAR ONE PRESS TRAVELS.

      A whole viewport is too much — the item you were looking at is gone and
      there is nothing to carry your eye across. Dawn's own sliders move by
      one item, which on a wide desktop rail means pressing six times.

      So: as many whole items as fit, less one, and never less than one. On a
      phone that is one card; on a desktop showing four it is three, leaving
      one on screen as an anchor. Measured from the live gap between the first
      two items rather than from a setting, so it stays right when the rail's
      own gap changes with the breakpoint.
    */
    stride() {
      const items = this.rail.children;
      if (!items.length) return this.rail.clientWidth;

      const first = items[0].getBoundingClientRect();
      let pitch = first.width;
      if (items.length > 1) {
        // Distance between two item starts: width plus whatever gap is live.
        const second = items[1].getBoundingClientRect();
        const measured = second.left - first.left;
        if (measured > 0) pitch = measured;
      }
      if (pitch <= 0) return this.rail.clientWidth;

      const fit = Math.floor(this.rail.clientWidth / pitch);
      return pitch * Math.max(1, fit - 1);
    }

    step(direction) {
      this.rail.scrollBy({
        left: this.stride() * direction,
        behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
      });
    }

    // The overlay is laid over the rail's own box. offsetTop and offsetLeft
    // are measured against the offset parent, and the offset parent IS the
    // rail's parent because connectedCallback made it `position: relative`.
    place() {
      this.style.top = this.rail.offsetTop + 'px';
      this.style.left = this.rail.offsetLeft + 'px';
      this.style.width = this.rail.offsetWidth + 'px';
      this.style.height = this.rail.offsetHeight + 'px';
      this.alignToMedia();
    }

    /*
      THE ARROWS LINE UP WITH THE PICTURE, NOT WITH THE RAIL.

      A product card is an image with a title and a price under it, so the
      middle of the CARD sits well below the middle of the IMAGE — on the
      gifting rail that put the arrows down level with the Add to cart
      button, overlapping the card rather than framing the row.

      Measured rather than guessed, because the text underneath is not a
      fixed height: a title that wraps to two lines, a price with a strike
      and a save badge, or a card with no compare price all move the card's
      centre while the image's centre stays where it is.

      The first image in the first card. Not the first <svg> — the wishlist
      heart is one — and not the card box, which is the thing we are trying
      not to use. Rails whose item IS a picture, like the occasion tiles and
      the Instagram strip, land on the same answer either way.

      ResizeObserver already watches the first item, so a lazy image arriving
      or a font swapping in re-runs this rather than leaving the arrows where
      the placeholder put them.
    */
    alignToMedia() {
      const first = this.rail.firstElementChild;
      const media = first && (first.matches('img') ? first : first.querySelector('img'));
      if (!media) {
        // Nothing to measure: the rail's own middle is the honest fallback.
        this.style.removeProperty('--rail-arrow-top');
        return;
      }

      const box = media.getBoundingClientRect();
      if (!box.height) return;

      const rail = this.rail.getBoundingClientRect();
      this.style.setProperty('--rail-arrow-top', box.top + box.height / 2 - rail.top + 'px');
    }

    update() {
      this.place();

      /*
        THE OVERFLOW TEST, AND THE ONE PIXEL OF SLACK.

        scrollWidth and clientWidth are rounded to integers from sub-pixel
        layout, so a rail that fits exactly reports a stray pixel of overflow
        often enough to matter — and that is the price-bands case: three items
        that fit, which must show no arrows at all rather than two that do
        nothing. Anything under two pixels is measurement noise, not content.
      */
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const overflows = max > 2;

      // Hidden rather than disabled when there is nothing to scroll. A
      // disabled button is still a button the eye has to dismiss; the design
      // is cleaner with nothing there, and it is what the three-item rail
      // should look like.
      this.classList.toggle('rail-nav--on', overflows);
      if (!overflows) return;

      // Direction-aware, so this is correct in a right-to-left locale where
      // scrollLeft runs negative.
      const at = Math.abs(this.rail.scrollLeft);
      this.prev.disabled = at <= 2;
      this.next.disabled = at >= max - 2;
    }
  }

  customElements.define('tyoh-rail', TyohRail);
}
